import { useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin } from "lucide-react";
import PropertyCard from "@/components/common/PropertyCard";
import PropertyMapView from "@/components/buyer/map/PropertyMapView";
import { getEffectiveStatus } from "@/utils/propertyStatus";

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  status: string;
  created_at: string;
  arv_value: number | null;
  property_type: string;
  closed_date: string | null;
  contract_expiration_date: string | null;
}

interface PropertySearchResultsProps {
  properties: Property[];
  viewMode: "list" | "map";
  searchLocation: string;
  onClearFilters: () => void;
  isSearchResults?: boolean;
  resultsTruncated?: boolean;
  totalCount?: number;
}

const PropertySearchResults = ({
  properties,
  viewMode,
  searchLocation,
  onClearFilters,
  isSearchResults = false,
  resultsTruncated = false,
  totalCount = 0
}: PropertySearchResultsProps) => {
  // Memoize daysOnSite calculation to prevent recreation on every render
  const daysOnSite = useCallback((createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, []);

  if (properties.length === 0 && viewMode === "list" && isSearchResults) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Properties Found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search criteria or expanding your location search.
            </p>
            <Button variant="outline" onClick={onClearFilters}>
              Clear Filters & Browse All Properties
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (properties.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">
            {isSearchResults ? "Search Results" : "Properties"}
          </h2>
          <Badge variant={resultsTruncated ? "destructive" : "secondary"}>
            {resultsTruncated 
              ? `Showing ${properties.length} of ${totalCount} properties - refine your search`
              : `${properties.length} properties found`
            }
          </Badge>
        </div>
        {isSearchResults && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            Back to Browse
          </Button>
        )}
      </div>

      {viewMode === "list" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
          {properties.map((property) => (
            <PropertyCard
              key={property.id}
              id={property.id}
              address={property.address}
              city={property.city}
              state={property.state}
              zip={property.zip_code}
              price={property.price}
              bedrooms={property.bedrooms}
              bathrooms={property.bathrooms}
              squareFeet={property.square_feet}
              imageUrl={property.image_url || "/placeholder.svg"}
              daysOnSite={daysOnSite(property.created_at)}
              status={getEffectiveStatus(property.status, property.closed_date, property.contract_expiration_date)}
            />
          ))}
        </div>
      )}

      {viewMode === "map" && (
        properties.length === 0 ? (
          <Card>
            <CardContent className="p-12">
              <div className="text-center">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Properties Match Your Filters</h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your search criteria to see properties in this area.
                </p>
                <Button variant="outline" onClick={onClearFilters}>
                  Clear Filters & View All Properties
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <PropertyMapView 
            properties={properties} 
            searchLocation={searchLocation}
          />
        )
      )}
    </div>
  );
};

export default PropertySearchResults;
